import { motion } from "framer-motion";

import { useParallax } from "../hooks/useParallax";

import { SanskritTerm } from "./SanskritTerm";
import { ScrollReveal } from "./ScrollReveal";
import { SectionHead } from "./SectionHead";

const SADGURU_IMAGE =
  "https://imagedelivery.net/Knvys86jJSy4RAoUACMsVg/649b28c8-f122-42b3-58ef-698f056bf300/publicMedium";

/**
 * The lineage — Sadguru Sadafal Deo Ji Maharaj and the Swarved
 * Mahamandir. Photography is presented as numbered plates in a book:
 * hairline frames, honest colour, small-caps captions.
 */
export function Origin() {
  const portrait = useParallax(-26);

  return (
    <section className="relative overflow-hidden bg-cream px-7 py-24 md:py-32">
      <div className="mx-auto max-w-6xl">
        <SectionHead index="01" label="The Origin" />

        <div className="mt-14 grid items-start gap-12 md:mt-20 md:grid-cols-12 md:gap-10">
          <ScrollReveal className="md:col-span-5">
            <motion.div ref={portrait.ref} style={{ y: portrait.y }}>
              <figure className="plate mx-auto max-w-sm md:mx-0">
                <img
                  src={SADGURU_IMAGE}
                  alt="Anant Shri Sadguru Sadafal Deo Ji Maharaj, the founder of Vihangam Yog"
                  className="aspect-[4/5] w-full object-cover object-top"
                />
                <figcaption className="plate-caption">
                  Plate I · Anant Shri Sadguru Sadafal Deo Ji Maharaj
                </figcaption>
              </figure>
            </motion.div>
          </ScrollReveal>

          <ScrollReveal delay={0.15} className="md:col-span-7 md:pt-4">
            <div className="space-y-7">
              <h2 className="font-display text-3xl leading-[1.28] text-warm-brown md:text-[2.1rem]">
                In 1924, after seventeen years of profound meditation, His
                Holiness{" "}
                <SanskritTerm
                  term="Sadguru"
                  devanagari="सद्गुरु"
                  pronunciation="sud-goo-roo"
                  meaning="The true Master — one who leads from darkness to light, from the unreal to the real."
                  lore="Only a Sadguru can anoint a Sadguru. The role and authority is unique — passed through direct spiritual realisation."
                />{" "}
                Sadafal Deo Ji Maharaj rediscovered this ancient science.
              </h2>
              <p className="drop-cap max-w-xl text-base leading-[1.8] text-warm-brown-light md:text-[1.0625rem]">
                He devoted His life to making it accessible to all — and
                authored the Swarved, the complete scripture of the science of
                consciousness, so this wisdom could never again be lost.
              </p>
              <p className="max-w-xl text-base leading-[1.8] text-warm-brown-light md:text-[1.0625rem]">
                The lineage continues through successive Masters, now reaching
                over 50 nations and transforming millions of lives through{" "}
                <SanskritTerm
                  term="Brahma Vidyā"
                  devanagari="ब्रह्म विद्या"
                  pronunciation="bruh-muh vid-yaa"
                  meaning="The supreme knowledge — the science of the Absolute, leading to direct experience of the highest truth."
                  lore="Described in the four Vedas, Brahma Vidyā is the path of direct experiential knowledge of the Supreme Soul."
                />
                .
              </p>
            </div>
          </ScrollReveal>
        </div>

        {/* The Mahamandir — carved ivory stone; the palette of this
            site is the palette of this building. */}
        <ScrollReveal>
          <div className="mt-28 max-w-2xl md:mt-36">
            <h3 className="font-display text-3xl leading-[1.22] text-warm-brown md:text-[1.9rem]">
              The Swarved Mahamandir
            </h3>
            <p className="mt-5 text-base leading-[1.8] text-warm-brown-light md:text-[1.0625rem]">
              Standing in Varanasi, the Swarved Mahamandir is one of the largest
              meditation centres in the world — carved, verse by verse, in ivory
              stone. Inaugurated in December 2023.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <div className="mt-12 grid gap-6 md:mt-14 md:grid-cols-3">
            <figure className="plate">
              <img
                src="/images/mahamandir/mahamandir-exterior.jpg"
                alt="Swarved Mahamandir exterior with intricate stone carvings"
                className="h-64 w-full object-cover"
              />
              <figcaption className="plate-caption">
                Plate II · The carved exterior
              </figcaption>
            </figure>
            <figure className="plate">
              <img
                src="/images/mahamandir/mahamandir-interior.jpg"
                alt="Swarved Mahamandir interior — marble sanctum with golden arch"
                className="h-64 w-full object-cover"
              />
              <figcaption className="plate-caption">
                Plate III · The sanctum
              </figcaption>
            </figure>
            <figure className="plate">
              <img
                src="/images/mahamandir/mahamandir-twilight.jpg"
                alt="Swarved Mahamandir illuminated at twilight"
                className="h-64 w-full object-cover"
              />
              <figcaption className="plate-caption">
                Plate IV · At twilight
              </figcaption>
            </figure>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            <figure className="plate">
              <img
                src="/images/carvings/carving-chariot.jpg"
                alt="Stone carving of chariot drawn by horses — the body as chariot, senses as horses"
                className="h-48 w-full object-cover"
              />
              <figcaption className="plate-caption">
                Plate V · The chariot of the body
              </figcaption>
            </figure>
            <figure className="plate">
              <img
                src="/images/carvings/carving-sage-meditation.jpg"
                alt="Stone carving of a sage in deep meditation"
                className="h-48 w-full object-cover"
              />
              <figcaption className="plate-caption">
                Plate VI · A sage in meditation
              </figcaption>
            </figure>
            <figure className="plate">
              <img
                src="/images/carvings/carving-elephants.jpg"
                alt="Intricately carved elephants at the base of the Swarved Mahamandir"
                className="h-48 w-full object-cover"
              />
              <figcaption className="plate-caption">
                Plate VII · Elephants at the base
              </figcaption>
            </figure>
          </div>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-warm-gray italic">
            Carvings from the Swarved Mahamandir — the journey of consciousness
            told in stone.
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}
